import React from 'react';

interface DataPoint {
  date: string;
  value: number;
  units: string;
  referenceMin?: number | null;
  referenceMax?: number | null;
  status?: string;
}

interface DataQualityDetailsProps {
  testName: string;
  dataPoints: DataPoint[];
  onClose: () => void;
}

export const DataQualityDetails: React.FC<DataQualityDetailsProps> = ({
  testName,
  dataPoints,
  onClose
}) => {
  const issues: Array<{ type: 'error' | 'warning'; message: string; date?: string }> = [];
  
  // Check each data point for issues
  dataPoints.forEach(point => {
    const testNameLower = testName.toLowerCase();
    
    // Check for zero values
    const neverZeroTests = [
      'hemoglobin', 'hematocrit', 'rbc', 'wbc', 'platelet count',
      'mcv', 'mch', 'mchc', 'glucose', 'creatinine', 'bun',
      'hgb', 'hb', 'hct'
    ];
    
    if (neverZeroTests.some(test => testNameLower.includes(test)) && point.value === 0) {
      issues.push({
        type: 'error',
        message: `Zero value detected for ${testName} on ${new Date(point.date).toLocaleDateString()}`,
        date: point.date
      });
    }
    
    // Check for missing reference ranges
    if (point.referenceMin === null || point.referenceMax === null) {
      issues.push({
        type: 'warning',
        message: `Missing reference range on ${new Date(point.date).toLocaleDateString()}`,
        date: point.date
      });
    }
    
    // Check for incorrect reference ranges
    if (point.referenceMin !== null && point.referenceMax !== null) {
      // MCHC checks
      if (testNameLower.includes('mchc')) {
        if (point.units === 'g/dL' && point.referenceMin !== undefined && point.referenceMax !== undefined && 
            (point.referenceMin > 100 || point.referenceMax > 100)) {
          issues.push({
            type: 'error',
            message: `MCHC reference range (${point.referenceMin}-${point.referenceMax} ${point.units}) is incorrect. Expected range: 32-36 g/dL`,
            date: point.date
          });
        } else if (point.units === 'g/L' && point.referenceMin !== undefined && point.referenceMax !== undefined &&
                   (point.referenceMin < 100 || point.referenceMax < 100)) {
          issues.push({
            type: 'error',
            message: `MCHC reference range (${point.referenceMin}-${point.referenceMax} ${point.units}) is incorrect. Expected range: 320-360 g/L`,
            date: point.date
          });
        }
      }
      
      // Hemoglobin checks
      if (testNameLower.includes('hemoglobin') || testNameLower === 'hgb' || testNameLower === 'hb') {
        if (point.units === 'g/dL' && point.referenceMin !== undefined && point.referenceMax !== undefined &&
            (point.referenceMin > 30 || point.referenceMax > 30)) {
          issues.push({
            type: 'error',
            message: `Hemoglobin reference range (${point.referenceMin}-${point.referenceMax} ${point.units}) is incorrect. Expected range: 12-17 g/dL`,
            date: point.date
          });
        } else if (point.units === 'g/L' && point.referenceMin !== undefined && point.referenceMax !== undefined &&
                   (point.referenceMin < 50 || point.referenceMax < 50)) {
          issues.push({
            type: 'error',
            message: `Hemoglobin reference range (${point.referenceMin}-${point.referenceMax} ${point.units}) is incorrect. Expected range: 120-170 g/L`,
            date: point.date
          });
        }
      }
      
      // Check for extreme values
      if (point.referenceMin !== undefined && point.referenceMax !== undefined &&
          (point.value < point.referenceMin / 10 || point.value > point.referenceMax * 10)) {
        issues.push({
          type: 'error',
          message: `Value (${point.value} ${point.units}) is extremely outside reference range (${point.referenceMin}-${point.referenceMax})`,
          date: point.date
        });
      }
    }
  });
  
  // Remove duplicate messages
  const uniqueIssues = issues.reduce((acc: typeof issues, issue) => {
    const exists = acc.some(i => i.message === issue.message);
    if (!exists) {
      acc.push(issue);
    }
    return acc;
  }, []);
  
  // Sort by type (errors first) then by date
  uniqueIssues.sort((a, b) => {
    if (a.type !== b.type) {
      return a.type === 'error' ? -1 : 1;
    }
    return 0;
  });
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Data Quality Issues - {testName}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {uniqueIssues.length === 0 ? (
            <p className="text-gray-500">No data quality issues detected.</p>
          ) : (
            <div className="space-y-4">
              {uniqueIssues.map((issue, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    issue.type === 'error'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-start">
                    {issue.type === 'error' ? (
                      <svg className="w-5 h-5 text-red-500 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-yellow-500 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    )}
                    <div className="flex-1">
                      <p className={`text-sm ${issue.type === 'error' ? 'text-red-800' : 'text-yellow-800'}`}>
                        {issue.message}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 mb-2">What to do:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Check the original PDF to verify if these values are correct</li>
              <li>• Use the inline editor to correct any incorrect values</li>
              <li>• Contact your lab if reference ranges seem incorrect</li>
              <li>• Zero values often indicate data entry errors</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};